import React, { useMemo } from 'react';
import ManCrossPageFooter from '../components/ManCrossPageFooter';

// ── Constants ──────────────────────────────────────────────────────────────────
const IASH_THRESHOLD = 65;

// ── Score semaphore (escala IASH) ──────────────────────────────────────────────
const getScoreConfig = (score) => {
    if (score < 50) return { color: '#C0392B', fontWeight: 700, badgeBg: '#F3202A', badgeText: '#fff' };
    if (score < 65) return { color: '#B85E00', fontWeight: 700, badgeBg: '#F48732', badgeText: '#fff' };
    if (score < 80) return { color: '#8A6200', fontWeight: 400, badgeBg: '#D4AC0D', badgeText: '#574400' };
    return           { color: '#62C190', fontWeight: 400, badgeBg: '#62C190', badgeText: '#fff' };
};

// ── Dim label helpers ──────────────────────────────────────────────────────────

/**
 * Etiqueta corta para la fila de la tabla: "D{N} PrimeraPalabra [Segunda]"
 * Ej: "D1 Bienestar Físico y Escucha Corporal" → "D1 Bienestar Físico"
 */
const dimTableLabel = (title, key) => {
    if (!title) return key;
    const raw     = title.split('/')[0].trim();
    const words   = raw.split(/\s+/).filter(Boolean);
    const codeIdx = words.findIndex(w => /^D\d+$/i.test(w));
    const code    = codeIdx >= 0 ? words[codeIdx] : '';
    const after   = words.slice(codeIdx >= 0 ? codeIdx + 1 : 0).filter(w => w.length >= 3);
    let label = after[0] || '';
    if (after[1] && (`${code} ${label} ${after[1]}`).length <= 20) {
        label += ` ${after[1]}`;
    }
    return code ? `${code} ${label}` : (label || key);
};

/**
 * Título de tarjeta del panel derecho: "PrimeraPalabra (D{N})"
 * Ej: "D2 Manejo Emocional y Autocontrol" → "Manejo (D2)"
 */
const dimCardTitle = (title, key) => {
    if (!title) return key;
    const raw     = title.split('/')[0].trim();
    const words   = raw.split(/\s+/).filter(Boolean);
    const codeIdx = words.findIndex(w => /^D\d+$/i.test(w));
    const code    = codeIdx >= 0 ? words[codeIdx] : '';
    const after   = words.slice(codeIdx >= 0 ? codeIdx + 1 : 0).filter(w => w.length >= 3);
    const name    = after[0] || words[0] || key;
    return code ? `${name} (${code})` : name;
};

/** Extrae solo el código D{N} del título. */
const dimCode = (title, key) => {
    if (!title) return key;
    return title.split(/\s+/).find(w => /^D\d+$/i.test(w)) || key;
};

// ── Data builders ──────────────────────────────────────────────────────────────

const buildDimRows = (iashTotals) => {
    const avgByDim = iashTotals?.average_by_dimension || {};
    return Object.entries(avgByDim)
        .map(([key, dim]) => {
            const score         = typeof dim?.average        === 'number' ? dim.average        : null;
            const weightPercent = typeof dim?.weight_percent === 'number' ? dim.weight_percent : null;
            const weightFraction = typeof dim?.weight        === 'number' ? dim.weight
                                 : weightPercent != null ? weightPercent / 100 : null;
            if (score == null) return null;

            const roundedScore = Math.round(score * 10) / 10;
            const gap          = Math.round((roundedScore - IASH_THRESHOLD) * 10) / 10;
            const impactDim    = roundedScore < IASH_THRESHOLD && weightPercent != null
                ? (IASH_THRESHOLD - roundedScore) * (weightPercent / 100)
                : 0;
            const title = dim?.title || key;

            return {
                key,
                title,
                tableLabel:   dimTableLabel(title, key),
                cardTitle:    dimCardTitle(title, key),
                code:         dimCode(title, key),
                score:        roundedScore,
                weightPercent: weightPercent != null ? Math.round(weightPercent) : null,
                weightFraction,
                gap,
                impactDim,
            };
        })
        .filter(Boolean)
        .sort((a, b) => b.impactDim - a.impactDim);  // mayor impacto primero → top 2
};

/** "D{N} y D{M} concentran el X% del déficit — y pesan el Y% del IASH." */
const buildCalloutText = (rows) => {
    if (rows.length < 2) return '';
    const totalImpact = rows.reduce((acc, r) => acc + r.impactDim, 0);
    if (totalImpact === 0) return '';
    const [d1, d2] = rows;
    const concPct = Math.round(((d1.impactDim + d2.impactDim) / totalImpact) * 100);
    const pesoSum = (d1.weightPercent || 0) + (d2.weightPercent || 0);
    return `${d1.code} y ${d2.code} concentran el ${concPct}% del déficit — y pesan el ${pesoSum}% del IASH.`;
};

/** Insight del panel derecho: peso combinado + relación con IVC. */
const buildRightInsight = (top2) => {
    if (top2.length < 2) return null;
    const pesoSum = (top2[0].weightPercent || 0) + (top2[1].weightPercent || 0);
    return `Ambas concentran el ${pesoSum}% del peso total y su fallo simultáneo eleva el IVC directamente.`;
};

/** Footer: agenda de intervención con los códigos de las top 2 dims. */
const buildFooterText = (top2) => {
    if (!top2.length) return '';
    const c1 = top2[0]?.code || '';
    const c2 = top2[1]?.code || '';
    if (!c2) return `${c1} es la dimensión prioritaria de intervención de autocuidado; las demás son complementarias y no reducirán el riesgo de accidente hoy.`;
    return `${c1} y ${c2} son la única agenda prioritaria de intervención de autocuidado; el resto es complementario y no reducirá el riesgo de accidente hoy.`;
};

// ── Component ──────────────────────────────────────────────────────────────────

/**
 * S10 — Dimensiones IASH
 *
 * Props:
 *  - reportData  : full report payload
 *  - headerText  : top-bar context string
 *  - pageNumber  : displayed page number
 */
const ManCrossPageDimIASH = ({ reportData, headerText, pageNumber }) => {
    const iashTotals = reportData?.payload?.totals?.iash || {};

    const rows       = useMemo(() => buildDimRows(iashTotals),                 [iashTotals]);
    const top2       = useMemo(() => rows.slice(0, 2),                         [rows]);
    const callout    = useMemo(() => buildCalloutText(rows),                   [rows]);
    const rightInsight = useMemo(() => buildRightInsight(top2),                [top2]);
    const footerText = useMemo(() => buildFooterText(top2),                    [top2]);
    const tableRows  = useMemo(() => [...rows].sort((a, b) => a.score - b.score), [rows]);

    return (
        <div className="mgcr-di-page">

            {/* Page header bar */}
            <div className="mgcr-page-header">
                <span className="mgcr-page-header-text">{headerText}</span>
                <span className="mgcr-page-header-num">{pageNumber}</span>
            </div>

            <div className="keep-blankspace" />

            {/* Title */}
            <div className="mgcr-dx-title-area">
                <h2 className="mgcr-dx-main-title">Dimensiones IASH</h2>
                <p  className="mgcr-dx-subtitle">Foco en Autocuidado</p>
            </div>

            {/* ── Main board ── */}
            <div className="mgcr-di-board">

                {/* LEFT: Cuadro General */}
                <div className="mgcr-di-left">

                    {/* Callout header row */}
                    <div className="mgcr-di-callout-row">
                        <div className="mgcr-di-section-label">Cuadro<br />General</div>
                        <div className="mgcr-di-callout-text">{callout}</div>
                    </div>

                    {/* Column headers */}
                    <div className="mgcr-di-col-header">
                        <div className="mgcr-di-ch-name">Dimensión IASH</div>
                        <div className="mgcr-di-ch-weight">Peso</div>
                        <div className="mgcr-di-ch-bar">
                            <div className="mgcr-di-axis">
                                {[0, 25, 50, 75, 100].map(v => (
                                    <span key={v}>{v}</span>
                                ))}
                            </div>
                            <span className="mgcr-dh-threshold-label">Umbral IASH: {IASH_THRESHOLD}</span>
                        </div>
                        <div className="mgcr-di-ch-score">Score</div>
                        <div className="mgcr-di-ch-gap">Brecha</div>
                    </div>

                    {/* Dimension rows — score ASC */}
                    <div className="mgcr-di-rows">
                        {tableRows.map((row) => {
                            const cfg    = getScoreConfig(row.score);
                            const gapNeg = row.gap < 0;

                            return (
                                <div key={row.key} className="mgcr-di-row">

                                    <div className="mgcr-di-name">
                                        <span className="mgcr-di-dim-title">{row.tableLabel}</span>
                                    </div>

                                    <div className="mgcr-di-weight">
                                        {row.weightPercent != null ? `${row.weightPercent}%` : '—'}
                                    </div>

                                    <div className="mgcr-di-bar-col">
                                        <div className="mgcr-di-bar-track">
                                            <div
                                                className="mgcr-di-bar-fill"
                                                style={{
                                                    width:      `${Math.min(Math.max(row.score, 0), 100)}%`,
                                                    background: cfg.badgeBg,
                                                    opacity:    0.85,
                                                }}
                                            />
                                            {/* Threshold line at 65% — override the default 75% via inline style */}
                                            <div
                                                className="mgcr-di-threshold-tick"
                                                style={{ left: `${IASH_THRESHOLD}%` }}
                                            />
                                        </div>
                                    </div>

                                    <div
                                        className="mgcr-di-score-val"
                                        style={{ color: cfg.color, fontWeight: cfg.fontWeight }}
                                    >
                                        {Math.round(row.score)}%
                                    </div>

                                    <div
                                        className="mgcr-di-gap"
                                        style={{
                                            background: gapNeg ? '#FFE5E5' : '#E8F8F0',
                                            color:      gapNeg ? '#F3202A' : '#62C190',
                                            fontWeight: gapNeg ? 700 : 400,
                                        }}
                                    >
                                        {row.gap > 0 ? `+${row.gap}` : row.gap}
                                    </div>

                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* RIGHT: Foco de Intervención */}
                <div className="mgcr-di-right">
                    <p className="mgcr-di-impact-header">Foco de Intervención</p>

                    <div className="mgcr-dh-cards">
                        {top2.map((row) => {
                            const cfg = getScoreConfig(row.score);
                            return (
                                <div
                                    key={row.key}
                                    className="mgcr-dh-card"
                                    style={{ background: cfg.badgeBg }}
                                >
                                    <p className="mgcr-dh-card-title">{row.cardTitle}</p>
                                    <p className="mgcr-dh-card-stat">Resultado {Math.round(row.score)}</p>
                                    <p className="mgcr-dh-card-stat">
                                        Peso {row.weightPercent != null ? `${row.weightPercent}%` : '—'}
                                    </p>
                                </div>
                            );
                        })}
                    </div>

                    {rightInsight && (
                        <p className="mgcr-dh-insight">
                            <strong>Insight:</strong>{' '}{rightInsight}
                        </p>
                    )}
                </div>

            </div>

            {/* Footer */}
            {footerText && (
                <div className="mgcr-insight-footer">
                    <strong>Esto implica que:</strong>{' '}{footerText}
                </div>
            )}

            <div className="keep-blankspace" />

            <ManCrossPageFooter />
        </div>
    );
};

export default ManCrossPageDimIASH;
